import type { Landmark } from '../../../data/types';
import type { LinearRGB } from '../../../core/color';
import { scaleColor } from '../../../core/color';
import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { gableRoof } from '../roofSolids';
import { horizontalCap, orientedFace, planPoint } from '../solids';
import { planBounds, planFrameOf, meshFrameOf, toLayout } from './planFrame';

export interface LastageGardenShedColors {
  readonly timber: LinearRGB;
  readonly roof: LinearRGB;
  readonly dark: LinearRGB;
}

interface Part {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

/** The long low garden shed has its own painted foot; it is not one of the five house fronts.
 * The pitched silhouette is observed. The dimensions, covering, framing and door are estimates. */
export function lastageGardenShedPlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const b = planBounds(plan);
  const uc = (b.u0 + b.u1) / 2,
    vc = (b.v0 + b.v1) / 2;
  const su = (b.u1 - b.u0) / 18,
    sv = (b.v1 - b.v0) / 6;
  const u = (value: number) => uc + value * su;
  const v = (value: number) => vc + value * sv;
  const part = (u0: number, u1: number, v0: number, v1: number): Part => ({
    u0: u(u0),
    u1: u(u1),
    v0: v(v0),
    v1: v(v1),
  });
  const eave = Number(landmark.params.height ?? 2.8);
  const ridge = Number(landmark.params.ridgeHeight ?? 4.7);
  const solids = [
    part(-8.65, 7.3, -2.7, -2.35),
    part(-8.65, 7.3, 2.35, 2.7),
    part(-8.65, -8.45, -2.7, 2.7),
    part(7.0, 7.32, -2.7, -1.3),
    part(7.0, 7.32, 1.3, 2.7),
    part(8.25, 8.55, -2.7, -2.4),
    part(8.25, 8.55, 2.4, 2.7),
  ];
  return { plan, frame: meshFrameOf(plan), u, v, part, solids, eave, ridge };
}

function block(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  part: Part,
  y0: number,
  y1: number
): void {
  builder.box(style, frame, [part.u0, y0, -part.v1], [part.u1, y1, -part.v0], {
    top: true,
    bottom: y0 > 0,
    sides: true,
  });
}

/** A solid square brace in a wall's u/y plane. Its feet stay on the side of the doorway. */
function brace(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  a: Vec2,
  b: Vec2,
  v: number,
  section: number
): void {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const h = section / 2,
    nu = (-(b[1] - a[1]) / length) * h,
    ny = ((b[0] - a[0]) / length) * h;
  const corners = ([u, y]: Vec2): Vec3[] => [
    planPoint(u - nu, y - ny, v - h),
    planPoint(u + nu, y + ny, v - h),
    planPoint(u + nu, y + ny, v + h),
    planPoint(u - nu, y - ny, v + h),
  ];
  const start = corners(a),
    end = corners(b);
  const centre = planPoint((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, v);
  const face = (points: Vec3[]) => {
    const midpoint: Vec3 = [
      points.reduce((sum, q) => sum + q[0], 0) / points.length,
      points.reduce((sum, q) => sum + q[1], 0) / points.length,
      points.reduce((sum, q) => sum + q[2], 0) / points.length,
    ];
    orientedFace(builder, style, frame, points, [
      midpoint[0] - centre[0],
      midpoint[1] - centre[1],
      midpoint[2] - centre[2],
    ]);
  };
  face(start);
  face(end);
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    face([start[i]!, start[j]!, end[j]!, end[i]!]);
  }
}

export function buildLastageGardenShed(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: LastageGardenShedColors,
  ids: { readonly edgeId: number; readonly seed: number }
): number {
  const before = builder.triangleCount,
    p = lastageGardenShedPlan(landmark);
  const style = (color: LinearRGB, surface: VertexStyle['surface']): VertexStyle => ({
    ...ids,
    color,
    surface,
    strokeAngle: Math.PI / 2,
    extent: [0, 0],
  });
  const timber = style(colors.timber, SURFACE.wood);
  const dark = style(colors.dark, SURFACE.wood);
  const roof = style(colors.roof, SURFACE.roofThatch);
  const post = { ...timber, color: scaleColor(colors.timber, 0.75) };
  const board = (part: Part, direction: 'u' | 'v', top = p.eave - 0.15) => {
    block(builder, dark, p.frame, part, 0, top);
    const low = direction === 'u' ? part.u0 : part.v0;
    const high = direction === 'u' ? part.u1 : part.v1;
    const count = Math.ceil((high - low) / 0.48);
    for (let i = 0; i < count; i++) {
      const a = low + ((high - low) * i) / count + 0.004;
      const b = low + ((high - low) * (i + 1)) / count - 0.004;
      const plank =
        direction === 'u'
          ? { ...part, u0: a, u1: b, v0: part.v0 - 0.015, v1: part.v1 + 0.015 }
          : { ...part, v0: a, v1: b, u0: part.u0 - 0.015, u1: part.u1 + 0.015 };
      block(
        builder,
        { ...timber, color: scaleColor(colors.timber, i % 3 === 0 ? 0.88 : 1) },
        p.frame,
        plank,
        0,
        top
      );
    }
  };
  board(p.part(-8.55, 7.2, -2.59, -2.49), 'u');
  board(p.part(-8.55, 7.2, 2.49, 2.59), 'u');
  board(p.part(-8.55, -8.47, -2.55, 2.55), 'v');
  board(p.part(7.14, 7.22, -2.55, -1.3), 'v');
  board(p.part(7.14, 7.22, 1.3, 2.55), 'v');

  // Open double workshop doors lie back against the inner side walls, preserving the 2.6 m gap.
  for (const side of [-1, 1]) {
    const v0 = side > 0 ? 2.38 : -2.44,
      v1 = side > 0 ? 2.44 : -2.38;
    board(p.part(5.8, 7.1, v0, v1), 'u', 2.45);
    block(
      builder,
      post,
      p.frame,
      p.part(7.04, 7.3, side > 0 ? 1.3 : -1.52, side > 0 ? 1.52 : -1.3),
      0,
      2.6
    );
  }
  block(builder, post, p.frame, p.part(7.04, 7.3, -2.65, 2.65), 2.45, p.eave);

  for (const side of [-1, 1]) {
    const wallV = side * 2.55;
    for (const at of [-8.45, -4.6, -0.75, 3.1, 7.15]) {
      block(
        builder,
        post,
        p.frame,
        p.part(at - 0.09, at + 0.09, wallV - 0.12, wallV + 0.12),
        0,
        p.eave
      );
      if (at < 7)
        brace(
          builder,
          post,
          p.frame,
          [p.u(at + 0.1), 1.9],
          [p.u(at + 1.05), p.eave - 0.13],
          p.v(wallV),
          0.13
        );
    }
    block(
      builder,
      post,
      p.frame,
      p.part(-8.6, 8.55, wallV - 0.1, wallV + 0.1),
      p.eave - 0.16,
      p.eave
    );
    block(builder, post, p.frame, p.part(8.25, 8.55, wallV - 0.15, wallV + 0.15), 0, p.eave);
    brace(builder, post, p.frame, [p.u(8.4), 1.7], [p.u(7.45), p.eave - 0.16], p.v(wallV), 0.14);
  }

  // A roof over the front bay is supported by the two braced posts, not a floating awning.
  gableRoof(builder, { wall: timber, roof }, p.frame, {
    u0: p.u(-8.65),
    u1: p.u(8.7),
    v0: p.v(-2.82),
    v1: p.v(2.82),
    eaveY: p.eave,
    ridgeY: p.ridge,
    ends: { start: true, end: true },
  });
  horizontalCap(
    builder,
    post,
    p.frame,
    [
      [p.u(-8.65), p.v(-2.82)],
      [p.u(8.7), p.v(-2.82)],
      [p.u(8.7), p.v(2.82)],
      [p.u(-8.65), p.v(2.82)],
    ],
    p.eave,
    false
  );
  return builder.triangleCount - before;
}

export function lastageGardenShedObstacles(landmark: Landmark): readonly Ring[] {
  const p = lastageGardenShedPlan(landmark);
  return p.solids.map((b) => {
    const ring: Ring = [
      [b.u0, b.v0],
      [b.u1, b.v0],
      [b.u1, b.v1],
      [b.u0, b.v1],
    ];
    return ring.map((q) => toLayout(p.plan, q));
  });
}
